import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs } from '../lib/api';
import { useAuth } from '../lib/auth';
import { compact, dateShort, dateTime, money, money0, pct, qty, unitLabel } from '../lib/format';
import { isoDay } from '../lib/dates';
import { usePaged } from '../lib/usePaged';
import { DateRangeBar, useDateRange } from '../components/DateRange';
import { PageHeader, Tabs } from '../components/Layout';
import {
  Drawer,
  Empty,
  ErrorState,
  Kpi,
  Loading,
  Meter,
  Modal,
  Pager,
  Select,
  StatusChip,
  TextInput,
  useToast,
} from '../components/ui';

interface Ingredient {
  id: string;
  code: string;
  name: string;
  unit: string;
  category: string;
  stock: number;
  reorderLevel: number;
  parLevel: number;
  avgCost: number;
  value: number;
  gstRate: number;
  isLow: boolean;
  parFillPct: number;
  suggestedOrderQty: number;
}

interface IngredientDetail extends Ingredient {
  shelfLifeDays: number;
  moves: Array<{
    id: string;
    type: string;
    quantity: number;
    rate: number;
    value: number;
    balanceAfter: number;
    reference: string;
    note: string | null;
    occurredAt: string;
  }>;
  usedIn: Array<{ menuItemId: string; name: string; quantity: number }>;
}

interface Move {
  id: string;
  name: string;
  code: string;
  unit: string;
  type: string;
  quantity: number;
  rate: number;
  value: number;
  balanceAfter: number;
  reference: string;
  note: string | null;
  occurredAt: string;
}

interface Summary {
  itemCount: number;
  stockValue: number;
  lowCount: number;
  suggestions: Ingredient[];
}

interface CostReport {
  consumption: number;
  wastage: number;
  wastagePct: number;
  purchases: number;
}

interface ExpiringLot {
  ingredientId: string;
  code: string;
  name: string;
  unit: string;
  quantity: number;
  stock: number;
  receipt: string;
  expiryDate: string;
  /** Negative once it is past. */
  daysLeft: number;
}

export function InventoryPage() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [tab, setTab] = useState<'stock' | 'ledger' | 'expiring'>('stock');
  const [expiryDays, setExpiryDays] = useState(7);
  const [lowOnly, setLowOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [movement, setMovement] = useState<{
    type: 'WASTAGE' | 'ADJUSTMENT';
    ingredientId?: string;
  } | null>(null);
  const [creating, setCreating] = useState(false);

  // Today unless somebody asks for another day. The range drives the wastage and
  // consumption figures and the ledger; the stock list is as it stood at the close
  // of its last day, which is simply "now" while that is today.
  const range = useDateRange('today');
  const { from, to } = range;
  const asOf = to && to < isoDay() ? to : undefined;

  const ingredients = useQuery({
    queryKey: ['ingredients', search, lowOnly, asOf],
    queryFn: () =>
      api.get<Ingredient[]>(
        `/api/ingredients${qs({ q: search, lowOnly: lowOnly || undefined, asOf })}`,
      ),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ['ingredients', 'summary', asOf],
    queryFn: () => api.get<Summary>(`/api/ingredients/summary${qs({ asOf })}`),
    placeholderData: keepPreviousData,
  });
  const cost = useQuery({
    queryKey: ['reports', 'cost', from, to],
    queryFn: () => api.get<CostReport>(`/api/reports/cost${qs({ from, to })}`),
    placeholderData: keepPreviousData,
  });
  const moves = useQuery({
    queryKey: ['stock', 'moves', from, to],
    queryFn: () => api.get<Move[]>(`/api/stock/moves${qs({ limit: 500, from, to })}`),
    placeholderData: keepPreviousData,
    enabled: tab === 'ledger',
  });
  const pagedMoves = usePaged(moves.data ?? [], { phone: 10, desktop: 25 });

  // Declared with the other queries, above the early returns: a hook after a
  // conditional return changes the hook order between renders, and React tears
  // the page down rather than rendering it.
  const expiring = useQuery({
    queryKey: ['inventory', 'expiring', expiryDays],
    queryFn: () => api.get<ExpiringLot[]>(`/api/inventory/expiring?days=${expiryDays}`),
    enabled: tab === 'expiring',
  });

  if (ingredients.isLoading) return <Loading label="Counting the store…" />;
  if (ingredients.error)
    return <ErrorState error={ingredients.error} onRetry={() => void ingredients.refetch()} />;

  const rows = ingredients.data ?? [];
  // "today", one day, or a span - for the labels on the figures that follow the range.
  const span =
    from === isoDay() && to === isoDay()
      ? 'today'
      : from && to && from !== to
        ? `${dateShort(from)} – ${dateShort(to)}`
        : dateShort(to || from || isoDay());

  return (
    <div className="stack">
      <PageHeader
        title="Inventory"
        subtitle="Raw stock, recipe depletion, wastage and valuation"
        actions={
          can('inventory:adjust') ? (
            <>
              <button className="btn" onClick={() => setMovement({ type: 'WASTAGE' })}>
                Record wastage
              </button>
              {can('inventory:manage') ? (
                <button className="btn primary" onClick={() => setCreating(true)}>
                  + New item
                </button>
              ) : null}
            </>
          ) : null
        }
      />

      <div className="card">
        <div className="filter-bar" style={{ paddingTop: 12 }}>
          <DateRangeBar range={range} presets={['today', '7d', '30d']} />
          {!range.isInitial ? (
            <button className="btn ghost" onClick={() => range.choose('today')}>
              Back to today
            </button>
          ) : null}
        </div>
        <p className="small muted" style={{ padding: '0 14px 12px' }}>
          {asOf
            ? `Stock levels are as they stood at the close of ${dateShort(asOf)}, worked back from the movement ledger. Costs and reorder levels are today's.`
            : 'Stock levels are live.'}{' '}
          Wastage, consumption and the ledger cover {span}.
        </p>
      </div>

      <div className="kpis">
        <Kpi
          label="Stock value"
          value={compact(summary.data?.stockValue ?? 0)}
          foot={`${summary.data?.itemCount ?? 0} tracked items`}
        />
        <Kpi
          label="Below reorder"
          value={summary.data?.lowCount ?? 0}
          foot={`${summary.data?.suggestions.length ?? 0} ready to order`}
          tone={(summary.data?.lowCount ?? 0) > 0 ? 'amber' : ''}
        />
        <Kpi
          label={`Wastage, ${span}`}
          value={money0(cost.data?.wastage ?? 0)}
          foot={`${pct(cost.data?.wastagePct ?? 0)} of consumption`}
          tone={(cost.data?.wastagePct ?? 0) > 3 ? 'red' : ''}
        />
        <Kpi
          label={`Consumed, ${span}`}
          value={compact(cost.data?.consumption ?? 0)}
          foot={`Purchases ${compact(cost.data?.purchases ?? 0)}`}
          tone="blue"
        />
      </div>

      <Tabs
        tabs={[
          { id: 'stock', label: 'Stock on hand' },
          { id: 'expiring', label: 'Expiring' },
          { id: 'ledger', label: 'Movement ledger' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'expiring' ? (
        <div className="card">
          <div className="card-head">
            <div className="seg">
              {[7, 14, 30].map((days) => (
                <button
                  key={days}
                  className={expiryDays === days ? 'active' : ''}
                  onClick={() => setExpiryDays(days)}
                >
                  {days} days
                </button>
              ))}
            </div>
            <span className="small muted">
              Oldest delivery first — use that crate before the next
            </span>
          </div>

          {expiring.isLoading ? (
            <Loading />
          ) : (expiring.data ?? []).length === 0 ? (
            <Empty icon="⛁" message={`Nothing goes off in the next ${expiryDays} days`} />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th className="num">Received</th>
                    <th className="num">On hand</th>
                    <th>Delivery</th>
                    <th>Expires</th>
                  </tr>
                </thead>
                <tbody>
                  {(expiring.data ?? []).map((lot, index) => (
                    <tr key={`${lot.receipt}-${lot.ingredientId}-${index}`}>
                      <td>
                        <div className="b">{lot.name}</div>
                        <div className="xs muted mono">
                          {lot.code} · {unitLabel(lot.unit)}
                        </div>
                      </td>
                      <td className="num">{qty(lot.quantity)}</td>
                      <td className="num">{qty(lot.stock)}</td>
                      <td className="xs mono muted">{lot.receipt}</td>
                      <td>
                        <span
                          className={`chip ${lot.daysLeft < 0 ? 'danger' : lot.daysLeft <= 2 ? 'warn' : ''}`}
                        >
                          {lot.expiryDate}
                        </span>
                        <div className="xs muted">
                          {lot.daysLeft < 0
                            ? `${Math.abs(lot.daysLeft)} day${Math.abs(lot.daysLeft) === 1 ? '' : 's'} ago`
                            : lot.daysLeft === 0
                              ? 'Today'
                              : `In ${lot.daysLeft} day${lot.daysLeft === 1 ? '' : 's'}`}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {tab === 'stock' ? (
        <div className="card">
          <div className="card-head">
            <div className="seg">
              <button className={!lowOnly ? 'active' : ''} onClick={() => setLowOnly(false)}>
                All
              </button>
              <button className={lowOnly ? 'active' : ''} onClick={() => setLowOnly(true)}>
                Below reorder
              </button>
            </div>
            <div className="search" style={{ width: 210 }}>
              <input
                className="input"
                placeholder="Search item or code"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {rows.length === 0 ? (
            <Empty icon="⛁" message="No stock items match" />
          ) : (
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Item</th>
                    <th>Category</th>
                    <th className="num">On hand</th>
                    <th style={{ width: 150 }}>Level</th>
                    <th className="num">Avg cost</th>
                    <th className="num">Value</th>
                    <th className="num">Reorder / Par</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => (
                    <tr key={item.id} className="clickable" onClick={() => setOpenId(item.id)}>
                      <td>
                        <div className="b">{item.name}</div>
                        <div className="xs muted mono">
                          {item.code} · {unitLabel(item.unit)}
                        </div>
                      </td>
                      <td className="dim">{item.category}</td>
                      <td className="num b">{qty(item.stock, item.unit)}</td>
                      <td>
                        <Meter
                          value={item.parFillPct}
                          tone={
                            item.isLow
                              ? item.stock <= item.reorderLevel / 2
                                ? 'danger'
                                : 'warn'
                              : ''
                          }
                        />
                        <div className="xs muted" style={{ marginTop: 3 }}>
                          {item.isLow ? 'Below reorder' : `${pct(item.parFillPct)} of par`}
                        </div>
                      </td>
                      <td className="num">{money(item.avgCost)}</td>
                      <td className="num">{money(item.value)}</td>
                      <td className="num dim">
                        {qty(item.reorderLevel)} / {qty(item.parLevel)}
                      </td>
                      <td className="right">
                        {can('inventory:adjust') ? (
                          <button
                            className="btn ghost sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setMovement({ type: 'ADJUSTMENT', ingredientId: item.id });
                            }}
                          >
                            Adjust
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}>{rows.length} item(s)</td>
                    <td className="num">
                      {money(rows.reduce((sum, item) => sum + item.value, 0))}
                    </td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      ) : (
        <div className="card">
          {moves.isLoading ? (
            <Loading />
          ) : (
            <div className="table-wrap">
              <table className="tbl tbl-stack">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Item</th>
                    <th>Type</th>
                    <th className="num">Quantity</th>
                    <th className="num">Rate</th>
                    <th className="num">Value</th>
                    <th className="num">Balance</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedMoves.rows.map((move) => (
                    <tr key={move.id}>
                      <td className="nowrap">{dateTime(move.occurredAt)}</td>
                      <td className="b">{move.name}</td>
                      <td>
                        <StatusChip status={move.type} />
                      </td>
                      <td className={`num ${move.quantity < 0 ? 'dim' : 'b'}`}>
                        {move.quantity > 0 ? '+' : ''}
                        {qty(move.quantity, move.unit)}
                      </td>
                      <td className="num dim">{money(move.rate)}</td>
                      <td className="num">{money(move.value)}</td>
                      <td className="num dim">{qty(move.balanceAfter)}</td>
                      <td className="small">
                        <span className="mono">{move.reference}</span>
                        {move.note ? <div className="xs muted">{move.note}</div> : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {!moves.isLoading && (moves.data ?? []).length === 0 ? (
            <Empty icon="≡" message={`No stock movements ${span === 'today' ? 'today' : `for ${span}`}`} />
          ) : null}
          {(moves.data ?? []).length >= 500 ? (
            <div className="form-note warn" style={{ margin: '12px 14px' }}>
              Showing the latest 500 movements in this range. Narrow the dates to see the rest.
            </div>
          ) : null}
          <Pager {...pagedMoves} noun="movements" onPage={pagedMoves.setPage} />
        </div>
      )}

      {openId ? <IngredientDrawer id={openId} onClose={() => setOpenId(null)} /> : null}

      {movement ? (
        <MovementModal
          type={movement.type}
          ingredientId={movement.ingredientId}
          ingredients={rows}
          onClose={() => setMovement(null)}
          onDone={() => {
            setMovement(null);
            void queryClient.invalidateQueries({ queryKey: ['ingredients'] });
            void queryClient.invalidateQueries({ queryKey: ['stock'] });
            void queryClient.invalidateQueries({ queryKey: ['reports'] });
          }}
        />
      ) : null}

      {creating ? (
        <NewIngredientModal
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            void queryClient.invalidateQueries({ queryKey: ['ingredients'] });
          }}
        />
      ) : null}
    </div>
  );
}

function IngredientDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const item = useQuery({
    queryKey: ['ingredient', id],
    queryFn: () => api.get<IngredientDetail>(`/api/ingredients/${id}`),
  });
  const data = item.data;

  return (
    <Drawer title={data ? `${data.name} (${data.code})` : 'Stock item'} onClose={onClose}>
      {item.isLoading || !data ? (
        <Loading />
      ) : (
        <div className="stack">
          <div className="kpis">
            <Kpi
              label="On hand"
              value={qty(data.stock, data.unit)}
              foot={`Reorder at ${qty(data.reorderLevel, data.unit)}`}
            />
            <Kpi
              label="Moving average cost"
              value={money(data.avgCost)}
              foot={`per ${unitLabel(data.unit)} · GST ${data.gstRate}%`}
            />
            <Kpi
              label="Stock value"
              value={money0(data.value)}
              foot={`Shelf life ${data.shelfLifeDays} days`}
              tone="blue"
            />
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Used in</h2>
              <span className="chip">{data.usedIn.length} recipe(s)</span>
            </div>
            {data.usedIn.length === 0 ? (
              <Empty message="Not linked to any recipe yet" />
            ) : (
              <div className="card-body row wrap gap-6">
                {data.usedIn.map((use) => (
                  <span className="chip" key={use.menuItemId}>
                    {use.name} · {qty(use.quantity, data.unit)}
                  </span>
                ))}
              </div>
            )}
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Movement history</h2>
            </div>
            <div className="table-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>When</th>
                    <th>Type</th>
                    <th className="num">Quantity</th>
                    <th className="num">Balance</th>
                    <th>Reference</th>
                  </tr>
                </thead>
                <tbody>
                  {data.moves.map((move) => (
                    <tr key={move.id}>
                      <td className="nowrap">{dateTime(move.occurredAt)}</td>
                      <td>
                        <StatusChip status={move.type} />
                      </td>
                      <td className="num">
                        {move.quantity > 0 ? '+' : ''}
                        {qty(move.quantity, data.unit)}
                      </td>
                      <td className="num dim">{qty(move.balanceAfter)}</td>
                      <td className="small mono">{move.reference}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </Drawer>
  );
}

function MovementModal({
  type,
  ingredientId,
  ingredients,
  onClose,
  onDone,
}: {
  type: 'WASTAGE' | 'ADJUSTMENT';
  ingredientId?: string;
  ingredients: Ingredient[];
  onClose: () => void;
  onDone: () => void;
}) {
  const { fail, push } = useToast();
  const [form, setForm] = useState({
    ingredientId: ingredientId ?? ingredients[0]?.id ?? '',
    quantity: 0,
    reason: '',
    note: '',
  });

  const isWastage = type === 'WASTAGE';

  const submit = useMutation({
    mutationFn: () => api.post('/api/stock/moves', { ...form, type }),
    onSuccess: () => {
      push(isWastage ? 'Wastage recorded' : 'Stock adjusted', 'ok');
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title={isWastage ? 'Record wastage' : 'Stock adjustment'}
      size="narrow"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className={`btn ${isWastage ? 'danger' : 'primary'}`}
            disabled={
              !form.ingredientId || !form.quantity || !form.reason.trim() || submit.isPending
            }
            onClick={() => submit.mutate()}
          >
            {isWastage ? 'Record wastage' : 'Post adjustment'}
          </button>
        </>
      }
    >
      <div className="col gap-16">
        <Select
          label="Item"
          value={form.ingredientId}
          onChange={(e) => setForm({ ...form, ingredientId: e.target.value })}
        >
          {ingredients.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name} · {qty(item.stock, item.unit)} on hand
            </option>
          ))}
        </Select>
        <TextInput
          label={isWastage ? 'Quantity wasted' : 'Quantity (negative reduces stock)'}
          type="number"
          step="0.001"
          value={form.quantity}
          onChange={(e) => setForm({ ...form, quantity: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Reason"
          value={form.reason}
          onChange={(e) => setForm({ ...form, reason: e.target.value })}
          placeholder={
            isWastage ? 'e.g. spoilage, spillage, staff meal' : 'e.g. physical count correction'
          }
        />
        {isWastage ? (
          <div className="chip warn">
            Wastage is valued at moving-average cost and shows in the cost report
          </div>
        ) : null}
      </div>
    </Modal>
  );
}

function NewIngredientModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const { fail, push } = useToast();
  const [form, setForm] = useState({
    name: '',
    unit: 'KG',
    category: 'Grocery',
    openingStock: 0,
    unitCost: 0,
    reorderLevel: 0,
    parLevel: 0,
    gstRate: 5,
    hsn: '2106',
    shelfLifeDays: 30,
  });

  const create = useMutation({
    mutationFn: () => api.post('/api/ingredients', form),
    onSuccess: () => {
      push('Stock item created', 'ok');
      onDone();
    },
    onError: fail,
  });

  return (
    <Modal
      title="New stock item"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={!form.name.trim() || create.isPending}
            onClick={() => create.mutate()}
          >
            Create
          </button>
        </>
      }
    >
      <div className="form-grid">
        <TextInput
          className="span2"
          label="Name *"
          autoFocus
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <Select
          label="Unit"
          value={form.unit}
          onChange={(e) => setForm({ ...form, unit: e.target.value })}
        >
          <option value="KG">Kilogram</option>
          <option value="LITRE">Litre</option>
          <option value="PIECE">Piece</option>
          <option value="GRAM">Gram</option>
          <option value="ML">Millilitre</option>
        </Select>
        <Select
          label="Category"
          value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
        >
          {[
            'Grocery',
            'Vegetables',
            'Dairy',
            'Butchery',
            'Bakery',
            'Spices',
            'Beverages',
            'Desserts',
          ].map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </Select>
        <TextInput
          label="Opening stock"
          type="number"
          step="0.001"
          value={form.openingStock}
          onChange={(e) => setForm({ ...form, openingStock: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Cost per unit (₹)"
          type="number"
          step="0.01"
          value={form.unitCost}
          onChange={(e) => setForm({ ...form, unitCost: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Reorder level"
          type="number"
          step="0.001"
          value={form.reorderLevel}
          onChange={(e) => setForm({ ...form, reorderLevel: Number(e.target.value) || 0 })}
        />
        <TextInput
          label="Par level"
          type="number"
          step="0.001"
          value={form.parLevel}
          onChange={(e) => setForm({ ...form, parLevel: Number(e.target.value) || 0 })}
        />
        <Select
          label="GST rate"
          value={String(form.gstRate)}
          onChange={(e) => setForm({ ...form, gstRate: Number(e.target.value) })}
        >
          {[0, 5, 12, 18].map((rate) => (
            <option key={rate} value={rate}>
              {rate}%
            </option>
          ))}
        </Select>
        <TextInput
          label="Shelf life (days)"
          type="number"
          value={form.shelfLifeDays}
          onChange={(e) => setForm({ ...form, shelfLifeDays: Number(e.target.value) || 0 })}
        />
      </div>
    </Modal>
  );
}
